import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, Download, X, Video } from 'lucide-react';

interface VideoPlayerProps {
  video: {
    id: string;
    filename: string;
    originalFilename: string;
    filePath: string;
    fileSize: number;
    durationSeconds: number;
    resolution: string;
    format: string;
    createdAt: string;
  };
  onClose?: () => void;
  isModal?: boolean;
  /** API path after /api/, e.g. videos/temp/combined-….mp4 */
  streamPath?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ video, onClose, isModal = false, streamPath }) => {
  const videoId = video.id;
  const videoDurationSeconds = video.durationSeconds;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoVideoUrl, setDemoVideoUrl] = useState<string | null>(null);

  const [videoObjectUrl, setVideoObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    let createdObjectUrl: string | null = null;
    console.log('Setting up video player for video:', videoId);
    console.log('Video durationSeconds:', videoDurationSeconds);
    
    // Set duration from video prop immediately
    if (videoDurationSeconds && videoDurationSeconds > 0) {
      console.log('Setting duration from video prop:', videoDurationSeconds);
      setDuration(videoDurationSeconds);
    } else {
      console.log('No valid duration in video prop:', videoDurationSeconds);
    }
    
    // Try to load the actual video instead of always showing demo mode
    setIsDemoMode(false);
    setIsLoading(true);
    
    // Fetch video as blob with authentication
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('No auth token, falling back to demo mode');
      setIsDemoMode(true);
      setDemoVideoUrl('https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4');
      setIsLoading(false);
      return;
    }

    // Use the API base URL - remove /api suffix if present since we're adding it
    const apiBaseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api$/, '');
    const streamUrl = streamPath
      ? `${apiBaseUrl}/api/${streamPath.replace(/^\//, '')}`
      : `${apiBaseUrl}/api/videos/${videoId}/stream`;
    
    console.log('Attempting to load video from:', streamUrl);
    
    // First, check if the response is JSON (demo mode) or a video stream
    fetch(streamUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => {
      console.log('Video fetch response:', response);
      console.log('Response status:', response.status);
      console.log('Response content-type:', response.headers.get('content-type'));
      
      if (!response.ok) {
        console.error('Video fetch failed:', response.status, response.statusText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type') || '';
      
      // Check if response is JSON (demo mode) or video
      if (contentType.includes('application/json')) {
        console.log('Response is JSON, parsing for demo mode info...');
        return response.json().then(json => {
          console.log('Demo mode JSON:', json);
          if (json.demoVideoUrl) {
            setIsDemoMode(true);
            setDemoVideoUrl(json.demoVideoUrl);
            setIsLoading(false);
            return null; // Signal that we're in demo mode
          }
          throw new Error('Unexpected JSON response');
        });
      }
      
      // It's a video stream, create blob URL
      console.log('Response is video stream, creating blob...');
      return response.blob();
    })
    .then(blob => {
      // If blob is null, we're in demo mode (already handled)
      if (!blob) {
        return;
      }
      
      console.log('Video blob received, size:', blob.size);
      console.log('Video blob type:', blob.type);
      
      // Check if blob is valid
      if (blob.size === 0) {
        console.error('Received empty video blob');
        throw new Error('Empty video blob received');
      }
      
      const objectUrl = URL.createObjectURL(blob);
      console.log('Object URL created:', objectUrl);
      createdObjectUrl = objectUrl;
      setVideoObjectUrl(objectUrl);
      setIsDemoMode(false);
      setIsLoading(false);
    })
    .catch(error => {
      console.error('Error loading video, falling back to demo mode:', error);
      setIsDemoMode(true);
      setDemoVideoUrl('https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4');
      setIsLoading(false);
    });

    // Cleanup the object URL created by this effect run.
    return () => {
      if (createdObjectUrl) {
        URL.revokeObjectURL(createdObjectUrl);
      }
    };
  }, [videoId, videoDurationSeconds, streamPath]);

  // Cleanup effect for object URL
  useEffect(() => {
    return () => {
      if (videoObjectUrl) {
        URL.revokeObjectURL(videoObjectUrl);
      }
    };
  }, [videoObjectUrl]);

  // Video event handlers
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    // Set initial duration from video prop as fallback
    if (videoDurationSeconds && videoDurationSeconds > 0) {
      console.log('Using duration from video prop:', videoDurationSeconds);
      setDuration(videoDurationSeconds);
    }

    // Force video to load metadata
    videoElement.load();

    // Set a timeout to use fallback duration if metadata doesn't load
    const metadataTimeout = setTimeout(() => {
      if (videoElement.duration === 0 || isNaN(videoElement.duration) || !isFinite(videoElement.duration)) {
        console.log('Metadata timeout - using fallback duration:', videoDurationSeconds);
        if (videoDurationSeconds && videoDurationSeconds > 0) {
          setDuration(videoDurationSeconds);
        }
      }
    }, 3000); // Wait 3 seconds for metadata to load

    const handleLoadedMetadata = () => {
      console.log('Video metadata loaded');
      console.log('Video duration:', videoElement.duration);
      console.log('Video readyState:', videoElement.readyState);
      console.log('Video videoWidth:', videoElement.videoWidth);
      console.log('Video videoHeight:', videoElement.videoHeight);
      
      // Clear the timeout since metadata loaded
      clearTimeout(metadataTimeout);
      
      // Use video duration if it's valid and not Infinity, otherwise use fallback
      if (videoElement.duration > 0 && !isNaN(videoElement.duration) && isFinite(videoElement.duration)) {
        console.log('Using video metadata duration:', videoElement.duration);
        setDuration(videoElement.duration);
      } else {
        console.log('Video metadata duration invalid, using fallback:', videoDurationSeconds);
        if (videoDurationSeconds && videoDurationSeconds > 0) {
          setDuration(videoDurationSeconds);
        }
      }
      
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      console.log('Time update:', videoElement.currentTime, '/', videoElement.duration);
      setCurrentTime(videoElement.currentTime);
    };

    const handlePlay = () => {
      console.log('Video started playing');
      setIsPlaying(true);
    };

    const handlePause = () => {
      console.log('Video paused');
      setIsPlaying(false);
    };

    const handleEnded = () => {
      console.log('Video ended');
      setIsPlaying(false);
      // Keep the final time instead of resetting to 0
      const videoElement = videoRef.current;
      if (videoElement) {
        setCurrentTime(videoElement.duration);
      }
    };

    const handleVolumeChange = () => {
      console.log('Volume changed:', videoElement.volume, 'muted:', videoElement.muted);
      setVolume(videoElement.volume);
      setIsMuted(videoElement.muted);
    };

    const handleError = (e: Event) => {
      console.error('Video error:', e);
      console.error('Video error details:', videoElement.error);
    };

    const handleCanPlay = () => {
      console.log('Video can play');
    };

    const handleLoadedData = () => {
      console.log('Video data loaded');
    };

    // Add event listeners
    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    videoElement.addEventListener('loadeddata', handleLoadedData);
    videoElement.addEventListener('canplay', handleCanPlay);
    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('play', handlePlay);
    videoElement.addEventListener('pause', handlePause);
    videoElement.addEventListener('ended', handleEnded);
    videoElement.addEventListener('volumechange', handleVolumeChange);
    videoElement.addEventListener('error', handleError);

    // Cleanup
    return () => {
      clearTimeout(metadataTimeout);
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement.removeEventListener('loadeddata', handleLoadedData);
      videoElement.removeEventListener('canplay', handleCanPlay);
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('play', handlePlay);
      videoElement.removeEventListener('pause', handlePause);
      videoElement.removeEventListener('ended', handleEnded);
      videoElement.removeEventListener('volumechange', handleVolumeChange);
      videoElement.removeEventListener('error', handleError);
    };
  }, [videoObjectUrl, videoDurationSeconds]);

  const togglePlay = () => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    if (isPlaying) {
      videoElement.pause();
    } else {
      videoElement.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const newTime = parseFloat(e.target.value);
    videoElement.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const toggleMute = () => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    videoElement.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const newVolume = parseFloat(e.target.value);
    videoElement.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleFullscreen = () => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    if (!isFullscreen) {
      videoElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      // Use the API base URL
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const downloadUrl = `${apiBaseUrl}/videos/${video.id}/download`;

      // Create a link and trigger download with authentication
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download =
        (video.originalFilename && video.originalFilename.replace(/\.webm$/i, '.mp4')) ||
        `video-${video.id}.mp4`;
      
      // Add authorization header using fetch instead
      fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Download failed: ${response.statusText}`);
        }
        return response.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Download error:', error);
        alert('Failed to download video. Please try again.');
      });
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download video. Please try again.');
    }
  };

  const formatTime = (time: number) => {
    if (isNaN(time) || !isFinite(time)) return '00:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const playerContent = (
    <div className="relative bg-black rounded-lg overflow-hidden w-full">
      {/* Demo Mode Message */}
      {isDemoMode ? (
        <div className="w-full aspect-video flex flex-col items-center justify-center bg-gray-900 text-white">
          <div className="text-center p-8">
            <div className="mb-4">
              <Video className="h-16 w-16 mx-auto text-blue-400 mb-4" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Demo Video Player</h3>
            <p className="text-gray-300 mb-4">
              This is a demonstration of the video player functionality.
            </p>
            <p className="text-sm text-gray-400 mb-6">
              In production, your actual ETR exposure therapy recordings would play here.
            </p>
            {demoVideoUrl && (
              <div className="space-y-2">
                <p className="text-sm text-gray-300">Sample video:</p>
                <video
                  src={demoVideoUrl}
                  controls
                  className="w-full max-w-md rounded aspect-video"
                  poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjI0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjMzMzIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkRlbW8gVmlkZW88L3RleHQ+PC9zdmc+"
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Video Element */}
          <video
            ref={videoRef}
            src={videoObjectUrl || undefined}
            className="w-full h-full object-contain"
            preload="metadata"
            onClick={togglePlay}
            style={{ minHeight: '400px' }}
          />

          {/* Loading Overlay */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          )}
        </>
      )}

      {/* Controls Overlay - Only show for real videos */}
      {!isDemoMode && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
          {/* Progress Bar */}
          <div className="mb-3">
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
              style={{
                background: duration > 0 ? 
                  `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(currentTime / duration) * 100}%, #4b5563 ${(currentTime / duration) * 100}%, #4b5563 100%)` :
                  '#4b5563'
              }}
            />
          </div>

          {/* Control Buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {/* Play/Pause Button */}
              <button
                onClick={togglePlay}
                className="text-white hover:text-blue-400 transition-colors"
              >
                {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
              </button>

              {/* Time Display */}
              <span className="text-white text-sm">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>

              {/* Volume Controls */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={toggleMute}
                  className="text-white hover:text-blue-400 transition-colors"
                >
                  {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-16 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* Video Info */}
              <div className="text-white text-sm">
                <div>{video.resolution}</div>
                <div>{formatFileSize(video.fileSize)}</div>
              </div>

              {/* Fullscreen Button */}
              <button
                onClick={toggleFullscreen}
                className="text-white hover:text-blue-400 transition-colors"
              >
                <Maximize className="h-5 w-5" />
              </button>

              {/* Download Button */}
              <button
                onClick={handleDownload}
                className="text-white hover:text-blue-400 transition-colors"
              >
                <Download className="h-5 w-5" />
              </button>

              {/* Close Button (for modal) */}
              {isModal && onClose && (
                <button
                  onClick={onClose}
                  className="text-white hover:text-red-400 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="w-full max-w-6xl max-h-[90vh] aspect-video">
          {playerContent}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full aspect-video">
      {playerContent}
    </div>
  );
};

export default VideoPlayer;